import {AccountControlError} from './account-controls-client.ts';
import type { ParseResult } from '../../document-backend/types';
import type { DocumentAnalysis, DocumentReview } from '../../document-backend/analysis';
import type { AssessmentState } from './crs';
import type { PathwayAnswers } from './pathways';

export interface CloudReportSnapshot {
  version: 1;
  /** The authenticated document job that produced this report, retained across history restores. */
  sourceJobId?: string;
  inputMode: 'manual' | 'documents';
  parsed: ParseResult;
  analysis: DocumentAnalysis;
  review: DocumentReview;
  assessment?: { profile: AssessmentState; answers: PathwayAnswers };
  manualInput?: { profile: AssessmentState; answers: PathwayAnswers };
  files: Array<{ documentId: string; fileId: string; name: string; type: string; size?: number; sha256?: string }>;
}
export interface CloudReportSummary { id: string; title: string; createdAt: string; updatedAt: string; crsScore?: number | null }
export interface CloudReport extends CloudReportSummary { snapshot: CloudReportSnapshot; deleting?: boolean }
/** Query callers own their bounded read retry; imperative recovery keeps the existing default. */
export type CloudReportReadOptions = { retry?: false };
export interface CloudStoredFile { id: string; name: string; type: string; size?: number }

export const cloudReportQueryKey = (id: string | null) => ['reports', 'snapshot', id] as const;
const API = '/api/account/reports';
const reportUrl = (id: string) => `${API}/${encodeURIComponent(id)}`;
const fileUrl = (reportId: string, fileId: string) => `${reportUrl(reportId)}/files/${encodeURIComponent(fileId)}`;
type CloudReportFailureCode = 'network' | 'unauthorized' | 'forbidden' | 'not_found' | 'too_large' | 'rate' | 'request' | 'invalid_response' | 'deleting';
const failureCopy: Record<CloudReportFailureCode, [string, string]> = {
  network: ['暂时无法连接，请稍后重试。', 'Unable to connect. Please retry shortly.'],
  unauthorized: ['登录已失效，请重新登录。', 'Your session has expired. Sign in again.'],
  forbidden: ['没有权限访问这份资料。', 'You do not have permission to access these records.'],
  not_found: ['这份报告或文件已不存在。', 'This report or file no longer exists.'],
  too_large: ['文件较大，暂时无法保存。', 'The file is too large to save.'],
  rate: ['请求较多，请稍后再试。', 'Too many requests. Please retry later.'],
  request: ['暂时无法完成，请重试。', 'Unable to complete this request. Please retry.'],
  invalid_response: ['暂时无法读取资料，请重试。', 'Unable to read these records. Please retry.'],
  deleting: ['这份报告正在删除，暂时无法恢复，请稍后重试。', 'This report is being deleted. Please retry later.'],
};
export class CloudReportError extends Error {
  readonly code: CloudReportFailureCode;
  readonly status?: number;
  constructor(code: CloudReportFailureCode, status?: number) {
    super(failureCopy[code][0]); this.name = 'CloudReportError'; this.code = code; this.status = status;
  }
  messageFor(language: 'zh' | 'en'): string { return failureCopy[this.code][language === 'zh' ? 0 : 1]; }
}

function retryPause(attempt: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(signal!.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, 500 * (attempt + 1) + Math.floor(Math.random() * 250));
    signal?.addEventListener('abort', abort, { once: true });
  });
}

async function privateRequest(token: string, url: string, init: RequestInit = {}, replaySafe = false, recoverTransport?: () => Promise<Response | null>, retry = true): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  for (let attempt = 0; ; attempt++) {
    init.signal?.throwIfAborted();
    const canRetry = replaySafe && retry && attempt < 2;
    let response: Response;
    try {
      response = await fetch(url, { ...init, headers, cache: 'no-store', credentials: 'omit' });
      // A replay-safe response body can be lost after its HTTP headers arrive.
      if (replaySafe && response.ok) response = new Response(await response.arrayBuffer(), { status: response.status, statusText: response.statusText, headers: response.headers });
    }
    catch (error) {
      init.signal?.throwIfAborted();
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      if (recoverTransport) {
        const recovered = await recoverTransport();
        init.signal?.throwIfAborted();
        if (recovered) return recovered;
      }
      if (canRetry) { await retryPause(attempt, init.signal ?? undefined); continue; }
      throw new CloudReportError('network', 0);
    }
    init.signal?.throwIfAborted();
    if (response.ok) return response;
    if (response.status === 401 && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('halo:session-expired', { detail: { requestAccessToken: token } }));
    }
    if ([409, 428, 503].includes(response.status)) {
      let failure: unknown;
      try {failure = await response.json();} catch { /* Retain the general transport failure. */ }
      const kind = failure && typeof failure === 'object' && 'error' in failure ? String(failure.error) : '';
      if (['policy_unavailable', 'policy_version_changed', 'policy_acknowledgement_required', 'account_deletion_pending'].includes(kind)) throw new AccountControlError(kind, response.status);
    }
    if (canRetry && [500, 502, 503, 504].includes(response.status)) {
      await response.body?.cancel().catch(() => {});
      await retryPause(attempt, init.signal ?? undefined);
      continue;
    }
    const code = response.status === 401 ? 'unauthorized'
      : response.status === 403 ? 'forbidden'
      : response.status === 404 ? 'not_found'
      : response.status === 413 ? 'too_large'
      : response.status === 429 ? 'rate' : 'request';
    throw new CloudReportError(code, response.status);
  }
}
async function readJson<T>(response: Response): Promise<T> {
  try { return await response.json() as T; }
  catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new CloudReportError('invalid_response');
  }
}

export async function listCloudReports(token: string, signal?: AbortSignal, options: CloudReportReadOptions = {}): Promise<CloudReportSummary[]> {
  return (await readJson<{ reports: CloudReportSummary[] }>(await privateRequest(token, API, { signal }, true, undefined, options.retry !== false))).reports;
}
export async function getLatestCloudReport(token: string, signal?: AbortSignal, options: CloudReportReadOptions = {}): Promise<CloudReport | null> {
  const value=await readJson<{report?:CloudReport|null}>(await privateRequest(token,API+'?latest=1',{signal},true,undefined,options.retry!==false));
  if(!value||value.report===undefined||value.report!==null&&(typeof value.report!=='object'||typeof value.report.id!=='string'))throw new CloudReportError('invalid_response');
  if(value.report?.deleting===true)throw new CloudReportError('deleting');
  return value.report;
}
export async function getCloudReport(token: string, id: string, signal?: AbortSignal, purpose: 'view' | 'restore' = 'view', options: CloudReportReadOptions = {}): Promise<CloudReport> {
  const report = (await readJson<{ report: CloudReport }>(await privateRequest(token, reportUrl(id) + (purpose === 'restore' ? '?restore=1' : ''), { signal }, true, undefined, options.retry !== false))).report;
  if (report.deleting === true) throw new CloudReportError('deleting');
  return report;
}
export async function saveCloudReport(token: string, report: { id?: string; title: string; snapshot: CloudReportSnapshot }, signal?: AbortSignal, requestId?: string): Promise<CloudReport> {
  return (await readJson<{ report: CloudReport }>(await privateRequest(token, API, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(report.id === undefined && requestId ? { 'Idempotency-Key': requestId } : {}) }, body: JSON.stringify(report), signal,
  }, report.id !== undefined || !!requestId))).report;
}
export async function uploadCloudFile(token: string, reportId: string, fileId: string, file: File, signal?: AbortSignal): Promise<CloudStoredFile> {
  const url = fileUrl(reportId, fileId), type = file.type || 'application/octet-stream', encodedName = btoa(encodeURIComponent(file.name));
  let sourceDigest: Promise<ArrayBuffer> | undefined;
  const recoverTransport = async (): Promise<Response | null> => {
    signal?.throwIfAborted();
    let response: Response;
    try { response = await privateRequest(token, url, { signal }, true); }
    catch (error) { if (error instanceof CloudReportError && error.code === 'not_found') return null; throw error; }
    signal?.throwIfAborted();
    if (response.headers.get('Content-Type') !== type || response.headers.get('X-File-Name') !== encodedName) return null;
    const bytes = await response.arrayBuffer(); signal?.throwIfAborted();
    if (bytes.byteLength !== file.size) return null;
    const [expected, actual] = await Promise.all([
      sourceDigest ??= file.arrayBuffer().then(bytes => crypto.subtle.digest('SHA-256', bytes)),
      crypto.subtle.digest('SHA-256', bytes),
    ]);
    signal?.throwIfAborted();
    const expectedBytes = new Uint8Array(expected);
    if (!new Uint8Array(actual).every((value, index) => value === expectedBytes[index])) return null;
    // This proves the readable original, not completion of its search-index update.
    return Response.json({ file: { id: fileId.toLowerCase(), name: file.name, type, size: bytes.byteLength } });
  };
  return (await readJson<{ file: CloudStoredFile }>(await privateRequest(token, url, {
    method: 'PUT', headers: { 'Content-Type': type, 'X-File-Name': encodedName }, body: file, signal,
  }, true, recoverTransport))).file;
}
export async function downloadCloudFile(token: string, reportId: string, fileId: string, signal?: AbortSignal, options: CloudReportReadOptions = {}): Promise<Blob> {
  const response = await privateRequest(token, fileUrl(reportId, fileId), { signal }, true, undefined, options.retry !== false);
  return response.blob();
}
export async function deleteCloudReport(token: string, id: string): Promise<void> {
  await privateRequest(token, reportUrl(id), { method: 'DELETE' });
}
